import { useMemo, useState } from 'react';
import {
  type UnfilledOrderRow,
  type UnfilledStage,
} from './tradingMock';
import { useTradingUnfilledQuery } from './tradingApi';
import './trading.css';

// 권위: data/reports/UNIMPLEMENTED_INVENTORY.md §4.3 — Unfilled Rollover Queue.
//   `trading_unfilled_orders` ACTIVE 행 (symbol / signal_kind / unfilled_qty /
//    stage / rollover_count / next_action_at).
//   S1 → S2 → S3 → D+2 시퀀스 진행 상태 시각화.
//
// 실 API: GET /api/v1/trading/unfilled (TradingController) — useTradingUnfilledQuery.
// 백엔드는 DB raw enum (S1_PLACED / S2_PENDING / S1_PENDING_ROLLOVER 등) 을 그대로 보내며
// tradingMappers.mapStage 가 UI 5종 (S1/S2/S3/D2_OPEN/DISCARDED) 으로 정규화한다.

const STAGE_ORDER: UnfilledStage[] = ['S1', 'S2', 'S3', 'D2_OPEN', 'DISCARDED'];

const STAGE_LABEL: Record<UnfilledStage, string> = {
  S1: 'S1 (장중 재시도)',
  S2: 'S2 (장종가 큐)',
  S3: 'S3 (다음날 시초)',
  D2_OPEN: 'D+2 시초',
  DISCARDED: '소멸',
};

interface Props {
  /** 테스트 주입용 — 미지정 시 API 호출. */
  rows?: UnfilledOrderRow[];
}

export default function TradingUnfilledPage({ rows: injected }: Props): JSX.Element {
  const [stageFilter, setStageFilter] = useState<UnfilledStage | 'ALL'>('ALL');

  const enabled = injected === undefined;
  const { data, isLoading, isError, error } = useTradingUnfilledQuery({}, { enabled });

  const rows = injected ?? data?.rows ?? [];

  const filtered = useMemo(() => {
    if (stageFilter === 'ALL') return rows;
    return rows.filter((r) => r.stage === stageFilter);
  }, [rows, stageFilter]);

  return (
    <div className="trading-page">
      <header className="trading-page__header">
        <h1 className="trading-page__title">미체결 잔량 큐</h1>
        <span className="trading-page__subtitle">trading_unfilled_orders · ACTIVE</span>
      </header>

      {!injected && isError && (
        <div className="trading-page__notice" role="alert">
          <strong>오류 —</strong>
          {(error as Error)?.message ?? '미체결 큐 조회 실패'}
        </div>
      )}

      <div className="trading-filter" role="group" aria-label="필터">
        <label className="trading-filter__field">
          <span>Stage</span>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as UnfilledStage | 'ALL')}
          >
            <option value="ALL">전체</option>
            {STAGE_ORDER.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <span className="trading-page__subtitle" style={{ marginLeft: 'auto' }}>
          {filtered.length} / {rows.length}건
        </span>
      </div>

      <section className="trading-card" aria-label="미체결 주문 목록">
        <header className="trading-card__header">
          <h2 className="trading-card__title">큐 진행 상태</h2>
        </header>
        <div className="trading-card__body">
          <table className="trading-table">
            <thead>
              <tr>
                <th>종목</th>
                <th>유형</th>
                <th className="num">잔여 수량</th>
                <th>Stage</th>
                <th>진행</th>
                <th className="num">Rollover</th>
                <th>다음 액션</th>
                <th>생성</th>
              </tr>
            </thead>
            <tbody>
              {!injected && isLoading && (
                <tr>
                  <td colSpan={8} className="trading-table__empty">
                    불러오는 중…
                  </td>
                </tr>
              )}
              {(!isLoading || injected) && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="trading-table__empty">
                    조건에 해당하는 잔량이 없습니다.
                  </td>
                </tr>
              )}
              {filtered.map((row) => (
                <tr key={row.rowId}>
                  <td>
                    <div>{row.symbolName}</div>
                    <code
                      style={{ color: 'var(--ink-faint)', fontSize: 'var(--text-xs)' }}
                    >
                      {row.symbol}
                    </code>
                  </td>
                  <td>
                    <span
                      className={`trading-badge trading-badge--${signalKindTone(row.signalKind)}`}
                    >
                      {row.signalKind}
                    </span>
                  </td>
                  <td className="num">{row.unfilledQty}</td>
                  <td>
                    <span className={`trading-badge trading-badge--${stageTone(row.stage)}`}>
                      {STAGE_LABEL[row.stage]}
                    </span>
                  </td>
                  <td aria-label={`stage progress ${row.stage}`}>
                    <StageProgress current={row.stage} />
                  </td>
                  <td className={`num ${row.rolloverCount >= 2 ? 'trading-num--down' : ''}`}>
                    ×{row.rolloverCount}
                  </td>
                  <td>
                    <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                      {formatDate(row.nextActionAt)}
                    </code>
                  </td>
                  <td>
                    <code
                      style={{
                        color: 'var(--ink-faint)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--text-xs)',
                      }}
                    >
                      {formatDate(row.createdAt)}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StageProgress({ current }: { current: UnfilledStage }): JSX.Element {
  const idx = STAGE_ORDER.indexOf(current);
  return (
    <div className="tp-progress" aria-hidden>
      {STAGE_ORDER.slice(0, 4).map((s, i) => (
        <span
          key={s}
          className={`tp-progress__pill${i <= idx ? ' tp-progress__pill--done' : ''}`}
        >
          {s.replace('D2_OPEN', 'D+2')}
        </span>
      ))}
    </div>
  );
}

function stageTone(stage: UnfilledStage): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (stage) {
    case 'S1':
      return 'neutral';
    case 'S2':
      return 'warn';
    case 'S3':
    case 'D2_OPEN':
      return 'danger';
    case 'DISCARDED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function signalKindTone(
  kind: 'BUY' | 'SELL' | 'STOP_LOSS' | 'TAKE_PROFIT',
): 'ok' | 'warn' | 'danger' | 'accent' {
  if (kind === 'BUY') return 'ok';
  if (kind === 'SELL') return 'warn';
  if (kind === 'STOP_LOSS') return 'danger';
  return 'accent';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
